import type { ReactElement } from "react";
import { COMPACT_CHIP_BUDGET } from "@/shared/userTaskChips.ts";
import { AppleIcon } from "@/components/icons/AppleIcon.tsx";
import { GmailIcon } from "@/components/icons/GmailIcon.tsx";
import { GoogleCalendarIcon } from "@/components/icons/GoogleCalendarIcon.tsx";
import { GoogleDriveIcon } from "@/components/icons/GoogleDriveIcon.tsx";
import { GoogleIcon } from "@/components/icons/GoogleIcon.tsx";
import { OutlookIcon } from "@/components/icons/OutlookIcon.tsx";
import { SlackIcon } from "@/components/icons/SlackIcon.tsx";
import { TeamsIcon } from "@/components/icons/TeamsIcon.tsx";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon.tsx";
import { ZoomIcon } from "@/components/icons/ZoomIcon.tsx";
import { cn } from "@/lib/utils.ts";
import type { SuggestedTaskCategoryResp, SuggestedTaskResp } from "@/types/suggestedTasks.ts";
import {
  type SuggestedTaskLogo,
  suggestedTaskEmoji,
  suggestedTaskLogo,
} from "@/shared/suggestedTaskIcons.ts";
import { fillPlatform } from "@/shared/suggestedTaskPrompt.ts";
import { PILL_STAGGER_MS } from "./pillStagger.ts";

const LOGO_CLASS = "size-5";

/**
 * Our drawing of each logo `suggestedTaskLogo` can name. Which a task gets is
 * decided in `shared/`, alongside native, so both apps mark a task the same.
 */
const LOGOS: Record<SuggestedTaskLogo, ReactElement> = {
  gmail: <GmailIcon className={LOGO_CLASS} />,
  googleCalendar: <GoogleCalendarIcon className={LOGO_CLASS} />,
  googleDrive: <GoogleDriveIcon className={LOGO_CLASS} />,
  google: <GoogleIcon className={LOGO_CLASS} />,
  outlook: <OutlookIcon className={LOGO_CLASS} />,
  apple: <AppleIcon className={LOGO_CLASS} />,
  slack: <SlackIcon className={LOGO_CLASS} />,
  teams: <TeamsIcon className={LOGO_CLASS} />,
  whatsapp: <WhatsAppIcon className={LOGO_CLASS} />,
  zoom: <ZoomIcon className={LOGO_CLASS} />,
};

/** One pill: a task and the category it was drawn from. */
export type SuggestedPill = {
  task: SuggestedTaskResp;
  categoryId: string;
  /** The drawn category's own emoji, used when the task carries none. */
  categoryEmoji?: string;
  /** What a click puts in the composer, when that differs from the pill's text. */
  prefill?: string;
};

/**
 * The lead task of the first `limit` categories. The server has already
 * shuffled the categories, so this is a random few that never repeats one.
 *
 * `limit` is what the user's own tasks left of the screen's slots, so a user
 * with tasks to show sees fewer suggestions rather than a longer column.
 */
export const pillsFromCategories = (
  categories: SuggestedTaskCategoryResp[],
  limit: number = COMPACT_CHIP_BUDGET.slots,
): SuggestedPill[] =>
  categories
    .flatMap(({ id, emoji, tasks: [lead] }) =>
      lead ? [{ task: lead, categoryId: id, categoryEmoji: emoji }] : [],
    )
    .slice(0, Math.max(0, limit));

/**
 * The pills to show: none while the first draw is in flight, so the fallback
 * never flashes up and is swapped out a moment later; the drawn three once it
 * lands; the fallback when it brings nothing.
 *
 * Takes `isLoading` rather than `isPending`: with the gate closed the query is
 * skipped and stays pending forever, which would hold back the fallback from
 * exactly the users it is for.
 */
export const pillsToShow = (
  categories: SuggestedTaskCategoryResp[],
  isLoading: boolean,
  fallback: SuggestedPill[],
  limit: number = COMPACT_CHIP_BUDGET.slots,
): SuggestedPill[] => {
  if (isLoading) return [];
  const drawn = pillsFromCategories(categories, limit);
  if (drawn.length > 0) return drawn;
  return fallback.slice(0, Math.max(0, limit));
};

/**
 * The platform's logo when the prompt names one, then the task's own emoji,
 * then its category's.
 *
 * Both emoji come down with the sample rather than from a table here. A table
 * keyed on category ids goes stale the moment the catalog is reorganized, and
 * does it silently: every pill keeps rendering, all of them the fallback.
 */
const PillIcon = ({ pill }: { pill: SuggestedPill }) => {
  const logo = suggestedTaskLogo(pill.task);
  return (
    <span aria-hidden="true" className="flex size-5 shrink-0 items-center justify-center text-base">
      {logo ? LOGOS[logo] : suggestedTaskEmoji(pill.task, pill.categoryEmoji)}
    </span>
  );
};

/**
 * Up to three suggested tasks stacked under the new-chat composer, each a pill
 * that hugs its text, shortest on top. Picking one prefills the composer rather than sending, so
 * it stays an opening move the user can edit.
 */
export const SuggestedTaskPills = ({
  pills,
  onSelect,
  startIndex = 0,
}: {
  pills: SuggestedPill[];
  onSelect: (prompt: string) => void;
  /** How many pills sit above these, so one stagger spans the whole column. */
  startIndex?: number;
}) => (
  <ul className="flex w-full flex-col items-start gap-2">
    {pills
      .map((pill) => ({ pill, prompt: fillPlatform(pill.task) }))
      // Shortest first, so the stack's right edge steps out in one direction
      // instead of zigzagging.
      .sort((a, b) => a.prompt.length - b.prompt.length)
      .map(({ pill, prompt }, index) => (
        <li
          key={pill.task.id}
          className="max-w-full animate-enter"
          style={{
            animationDelay: `${(startIndex + index) * PILL_STAGGER_MS}ms`,
            animationFillMode: "both",
          }}
        >
          <button
            type="button"
            onClick={() => onSelect(pill.prefill ?? prompt)}
            // The Omnibox holds focus on this surface; a mousedown default would
            // pull it away before the click lands, and the prefill would arrive
            // at a field the cursor had just left.
            onMouseDown={(e) => e.preventDefault()}
            className={cn(
              // `rounded-3xl`, not `rounded-full`: fully round at one line's height,
              // and still a soft card if a long prompt wraps at a narrow width or
              // a large text size, where a capsule would pinch.
              "flex max-w-full items-center gap-3 rounded-3xl border bg-card py-2.5 pr-5 pl-4",
              "text-left text-sm text-foreground shadow-xs",
              "transition-colors duration-200 hover:bg-secondary",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
            )}
            data-statsig-name="new-chat-suggested-task"
            data-statsig-task-id={pill.task.id}
          >
            <PillIcon pill={pill} />
            {/* Two lines, then an ellipsis. A pill that wraps freely would let
                one dynamic task take most of the screen, and three of them
                leave no canvas at all; two lines is the ceiling that still
                shows a long task without the stack swallowing the page. What a
                click sends is the whole prompt either way. */}
            <span className="line-clamp-2">{prompt}</span>
          </button>
        </li>
      ))}
  </ul>
);
